import React, { useEffect, useState } from 'react';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import Card, { CardActions, CardBody, CardHeader, CardLabel, CardTitle } from '../bootstrap/Card';
import FormGroup from '../bootstrap/forms/FormGroup';
import Input from '../bootstrap/forms/Input';
import Button, { ButtonGroup } from '../bootstrap/Button';
import { getCurrentDateAndTime } from '../../services/common.service';
import { getProductAssignReport } from '../../services/reportService';
import { toasts } from '../../services/toaste.service';
import { TableLoader } from '../../services/loader.services';
import Icon from '../icon/Icon';
import dayjs from 'dayjs';
import Collapse from '../bootstrap/Collapse';
import OpenCardComponent from '../../common/components/OpenCardComponent';
import useSortableData from '../../hooks/useSortableData';
import PaginationButtons, { dataPagination } from '../PaginationButtons';
import ExportDropdown from '../../common/components/ExportDropdown';
import NoDataMsg from '../../common/components/NoDataMsg';
import { getSubModuleType } from '../../services/productService';
import SearchableSelect from '../../common/components/SearchableSelect';

const ProductAssignReport = () => {
    const [fromDate, setFromDate] = useState<string>(getCurrentDateAndTime('date'));
    const [toDate, setToDate] = useState<string>(getCurrentDateAndTime('date'));
    const [reportData, setReportData] = useState<any[]>([]);
    const [isLoader, setIsLoader] = useState<boolean>(false);
    const [dataStatus, setDataStatus] = useState<boolean>(false);
    const [noDataMsg, setNoDataMsg] = useState<string>('');
    const [isOpenListCard, setIsOpenListCard] = useState<boolean>(true);
    const [searchInput, setSearchInput] = useState<string>('');

    const [lotList, setLotList] = useState<any[]>([]);
    const [countList, setCountList] = useState<any[]>([]);
    const [lotId, setLotId] = useState<any>({ value: 0, label: 'All' });
    const [countId, setCountId] = useState<any>(null);

    useEffect(() => {
        getSubModuleType(1, (res) => {
            if (res?.data?.success && res?.data?.data?.subModuleType) {
                const data = res.data.data.subModuleType;
                const formatted = data.map((i: any) => ({ value: i.subModuleTypeId, label: i.subModuleType }));
                setLotList(formatted);
                if (formatted.length === 0) {
                    setLotId(null);
                }
            } else {
                setLotList([]);
                setLotId(null);
            }
        }, (err) => toasts(err, "Error"));

        getSubModuleType(2, (res) => {
            if (res?.data?.success && res?.data?.data?.subModuleType) {
                const data = res.data.data.subModuleType;
                const formatted = data.map((i: any) => ({ value: i.subModuleTypeId, label: i.subModuleType }));
                setCountList(formatted);
                if (formatted.length === 0) {
                    setCountId(null);
                } else {
                    setCountId({ value: 0, label: 'All' });
                }
            } else {
                setCountList([]);
                setCountId(null);
            }
        }, (err) => toasts(err, "Error"));
    }, []);

    const handleLotChange = (val: any) => {
        const selectedLot = val || { value: 0, label: 'All' };
        setLotId(selectedLot);
        
        if (selectedLot.value === 0) {
            setCountId({ value: 0, label: 'All' });
        } else {
            if (countList.length > 0) {
                setCountId({ value: 0, label: 'All' });
            } else {
                setCountId(null);
            }
        }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const { items, requestSort, getClassNamesFor } = useSortableData(reportData);
    const onCurrentPageData = dataPagination(items, currentPage, perPage);

    const filteredData = onCurrentPageData.filter((item: any) =>
        item.companyName?.toLowerCase().includes(searchInput.toLowerCase()) ||
        item.vehicleNo?.toLowerCase().includes(searchInput.toLowerCase()) ||
        item.lotNo?.toLowerCase().includes(searchInput.toLowerCase()) ||
        item.countNo?.toLowerCase().includes(searchInput.toLowerCase())
    );

    const getRowSpans = (data: any[]) => {
        const spans: number[] = [];
        let i = 0;
        while (i < data.length) {
            const currentItem = data[i];
            const currentKey = `${currentItem.companyName}-${currentItem.vehicleNo}-${currentItem.assignedDate}-${currentItem.assignedTime}`;
            let count = 1;
            while (
                i + count < data.length &&
                `${data[i + count].companyName}-${data[i + count].vehicleNo}-${data[i + count].assignedDate}-${data[i + count].assignedTime}` === currentKey
            ) {
                count++;
            }
            spans[i] = count;
            for (let j = 1; j < count; j++) {
                spans[i + j] = 0;
            }
            i += count;
        }
        return spans;
    };

    const rowSpans = getRowSpans(filteredData);

    const columnVisibilityData = [
        { columnName: "S.No", keyName: "sno" },
        { columnName: "Company", keyName: "companyName" },
        { columnName: "Vehicle", keyName: "vehicleNo" },
        { columnName: "Date", keyName: "assignedDate" },
        { columnName: "Time", keyName: "assignedTime" },
        { columnName: "Lot", keyName: "lotNo" },
        { columnName: "Count", keyName: "countNo" },
        { columnName: "S.Color", keyName: "blend" },
        { columnName: "Assigned", keyName: "assignedCount" },
        { columnName: "Scanned", keyName: "scannedCount" },
        { columnName: "Pending", keyName: "pendingCount" }
    ];

    const viewReport = () => {
        setIsLoader(true);
        setDataStatus(false);
        setReportData([]);
        setNoDataMsg('');

        const fromDateMs = dayjs(fromDate).startOf('day').valueOf();
        const toDateMs = dayjs(toDate).endOf('day').valueOf();
        const lId = lotId?.value || 0;
        const cId = countId?.value || 0;

        getProductAssignReport(
            fromDateMs,
            toDateMs,
            lId,
            cId,
            (response) => {
                setIsLoader(false);
                if (response.data.success) {
                    const data = response.data.data.productAssignReport;
                    if (data && data.length > 0) {
                        const flattenedData = data.flatMap((report: any) =>
                            report.productList.map((product: any, pIndex: number) => ({
                                ...product,
                                companyName: report.companyName,
                                vehicleNo: report.vehicleNo,
                                assignedDate: report.assignedDate,
                                assignedTime: report.assignedTime,
                            }))
                        );
                        for (let i = 0; i < flattenedData.length; i++) {
                            flattenedData[i].sno = i + 1;
                        }
                        setReportData(flattenedData);
                        setDataStatus(true);
                        setIsOpenListCard(false);
                    } else {
                        setNoDataMsg(data.message);
                        setDataStatus(true);
                        setIsOpenListCard(false);
                    }
                } else {
                    setNoDataMsg(response.data.message);
                    setDataStatus(true);
                    setIsOpenListCard(false);
                }
            },
            (error) => {
                setIsLoader(false);
                toasts(error, 'Error');
                setNoDataMsg('An error occurred while fetching the report.');
                setDataStatus(true);
                setIsOpenListCard(false);
            }
        );
    };

    return (
        <PageWrapper title='Product Assign Report'>
            <Page>
                <Collapse isOpen={isOpenListCard}>
                    <Card stretch className='d-print-none mb-0'>
                        <CardHeader borderSize={1}>
                            <CardLabel icon='Assignment' iconColor='info'>
                                <CardTitle>Product Assign Report</CardTitle>
                            </CardLabel>
                        </CardHeader>
                        <CardBody>
                            <div className='row g-4'>
                                <div className='col-lg-2'>
                                    <FormGroup id='fromDate' label='From Date' isFloating>
                                        <Input
                                            type='date'
                                            placeholder='From Date'
                                            onChange={(e: any) => setFromDate(e.target.value)}
                                            value={fromDate}
                                            max={getCurrentDateAndTime('date')}
                                        />
                                    </FormGroup>
                                </div>
                                <div className='col-lg-2'>
                                    <FormGroup id='toDate' label='To Date' isFloating>
                                        <Input
                                            type='date'
                                            placeholder='To Date'
                                            onChange={(e: any) => setToDate(e.target.value)}
                                            value={toDate}
                                            max={getCurrentDateAndTime('date')}
                                        />
                                    </FormGroup>
                                </div>
                                <div className='col-lg-3'>
                                    <FormGroup id='lot' label='Lot No' isFloating>
                                        <SearchableSelect
                                        isFloating
                                            id='lot'
                                            ariaLabel='Lot'
                                            placeholder='Select Lot'
                                            value={lotId}
                                            onChange={handleLotChange}
                                            list={lotList.length > 0 ? [{ value: 0, label: 'All' }, ...lotList] : []}
                                        />
                                    </FormGroup>
                                </div>
                                <div className='col-lg-3'>
                                    <FormGroup id='count' label='Count No' isFloating>
                                        <SearchableSelect
                                            isFloating
                                            id='count'
                                            ariaLabel='Count'
                                            placeholder='Select Count'
                                            value={countId}
                                            onChange={(val: any) => setCountId(val || { value: 0, label: 'All' })}
                                            list={countList.length > 0 ? [{ value: 0, label: 'All' }, ...countList] : []}
                                        />
                                    </FormGroup>
                                </div>
                                <div className='col-lg-2 d-flex align-items-center mt-4'>
                                    <Button
                                        color='primary'
                                        icon='Search'
                                        onClick={viewReport}
                                        className='mt-2'>
                                        View
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Collapse>

                <div className="mt-3"></div>

                <OpenCardComponent isOpenListCard={isOpenListCard} setIsOpenListCard={setIsOpenListCard} isLoader={isLoader} />

                {dataStatus && (
                    <Card stretch className='shadow-sm'>
                        <CardHeader borderSize={1}>
                            <CardLabel icon='List' iconColor='primary'>
                                <CardTitle>Report Data</CardTitle>
                            </CardLabel>
                            <CardActions>
                                <div className='d-flex align-items-center gap-2'>
                                    <ButtonGroup className='d-print-none' color='primary'>
                                        <Icon className='mt-1' icon='Search' size='2x' color='primary' />
                                        <Input
                                            id='tableSearch'
                                            type='search'
                                            placeholder='Search...'
                                            onChange={(e: any) => setSearchInput(e.target.value)}
                                            value={searchInput}
                                        />
                                    </ButtonGroup>
                                    <ExportDropdown fileName='Product_Assign_Report' reportData={reportData} columnVisibilityData={columnVisibilityData} />
                                </div>
                            </CardActions>
                        </CardHeader>
                        <CardBody className='p-3' id="print-container">
                            <div className='table-responsive'>
                                <table className='table table-modern table-hover text-uppercase tiny' style={{ minWidth: '1000px' }}>
                                    <thead className='table-primary'>
                                        <tr>
                                            <th className='ps-4 text-center small' style={{ width: '50px' }}>S.No</th>
                                            <th className='small'>Company</th>
                                            <th className='small'>Vehicle</th>
                                            <th className='small'>Date & Time</th>
                                            <th className='small'>Lot</th>
                                            <th className='small'>Count</th>
                                            <th className='small'>S.Color</th>
                                            <th className='text-center small'>Assigned</th>
                                            <th className='text-center small'>Scanned</th>
                                            <th className='text-center pe-4 small'>Pending</th>
                                        </tr>
                                    </thead>
                                    {filteredData.length > 0 ? (
                                        <tbody>
                                            {filteredData.map((item: any, index: number) => {
                                                const span = rowSpans[index];
                                                return (
                                                    <tr key={`${item.companyName}-${item.vehicleNo}-${index}`} className='border-bottom border-light'>
                                                        <td className='ps-4 text-center text-muted small align-middle'>{(currentPage - 1) * perPage + index + 1}</td>
                                                        {span > 0 && (
                                                            <td rowSpan={span} className='align-middle'>
                                                                <div className='small'>{item.companyName}</div>
                                                            </td>
                                                        )}
                                                        {span > 0 && (
                                                            <td rowSpan={span} className='align-middle'>
                                                                <div className='small'>{item.vehicleNo}</div>
                                                            </td>
                                                        )}
                                                        {span > 0 && (
                                                            <td rowSpan={span} className='align-middle'>
                                                                <div className='small'>{item.assignedDate} {item.assignedTime}</div>
                                                            </td>
                                                        )}
                                                        <td className='align-middle'>
                                                            <div className='small'>{item.lotNo}</div>
                                                        </td>
                                                        <td className='align-middle'>
                                                            <div className='small d-flex align-items-center gap-2'>
                                                                <span>{item.countNo}</span>
                                                                {(item.duplicateFlag === true || item.duplicateFlag === 1 || item.duplicateFlag === 'true') && (
                                                                    <Icon icon='Warning' color='danger' size='lg' title='Duplicate Product' />
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className='align-middle'>
                                                            <div className='small'>{item.blend}</div>
                                                        </td>
                                                        <td className='text-center align-middle'>
                                                            <span className='fw-semibold small text-primary '>{item.assignedCount}</span>
                                                        </td>
                                                        <td className='text-center align-middle'>
                                                            <span className='fw-semibold small text-success'>{item.scannedCount}</span>
                                                        </td>
                                                        <td className='text-center pe-4 align-middle'>
                                                            <span className={`fw-semibold small ${item.pendingCount > 0 ? 'text-danger' : 'text-muted'}`}>
                                                                {item.pendingCount}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    ) : (
                                        <NoDataMsg columnsCount={10} msg={noDataMsg} />
                                    )}
                                </table>
                            </div>
                        </CardBody>
                        <PaginationButtons
                            data={items}
                            label='items'
                            setCurrentPage={setCurrentPage}
                            currentPage={currentPage}
                            perPage={perPage}
                            setPerPage={setPerPage}
                            className="d-print-none"
                        />
                    </Card>
                )}

            </Page>
        </PageWrapper>
    );
};

export default ProductAssignReport;